import React from 'react';
import { Link } from 'react-router-dom';
import { Award, Users, Shield, Target, ArrowRight, CheckCircle, TrendingUp, Heart } from 'lucide-react';

const About = () => {
  const values = [
    {
      icon: <Shield className="h-8 w-8" />,
      title: "Trust & Transparency",
      description: "We believe in complete transparency in all our dealings and maintain the highest standards of integrity."
    },
    {
      icon: <Users className="h-8 w-8" />,
      title: "Customer First",
      description: "Our customers are at the heart of everything we do. We strive to exceed their expectations."
    },
    {
      icon: <Target className="h-8 w-8" />,
      title: "Excellence",
      description: "We are committed to delivering exceptional service and innovative financial solutions."
    },
    {
      icon: <Heart className="h-8 w-8" />,
      title: "Innovation Driven",
      description: "Digital-first approach with modern financial tools to serve you better."
    }
  ];

  const achievements = [
    { number: '29+', label: 'Years of Experience' },
    { number: '10,000+', label: 'Happy Customers' },
    { number: 'Substantial', label: 'Loans Disbursed' },
    { number: '99.5%', label: 'Customer Satisfaction' }
  ];

  const whyChoose = [
    {
      title: "Established in 1996",
      description: "Over 29 years of financial excellence and trust in the industry."
    },
    {
      title: "RBI Registered NBFC",
      description: "Fully compliant with national financial regulations and guidelines."
    },
    {
      title: "Customer First Approach",
      description: "Personalized, fast, and reliable service tailored to your needs."
    },
    {
      title: "Innovation Driven",
      description: "Digital-first approach with modern financial tools and secure platforms."
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-blue-50 to-indigo-100 py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto text-center">
          <div className="space-y-8">
            <div className="flex justify-center mb-8">
              <img 
                src="https://customer-assets.emergentagent.com/job_persian-indigo-fin/artifacts/d9xink6u_2897_Logo_V-PNG-01.png" 
                alt="PFIL Finance Logo" 
                className="h-32 w-auto"
              />
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 leading-tight">
              About <span style={{ color: '#340C7F' }}>PFIL Finance</span>
            </h1>
            <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
              Pooja Finstock International Limited (PFIL), established in 1996, is a trusted and RBI-registered 
              Non-Banking Financial Company (NBFC), delivering innovative financial solutions for over two decades.
            </p>
            <div className="flex justify-center">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
                {achievements.map((achievement, index) => (
                  <div key={index} className="text-center">
                    <div className="text-3xl font-bold" style={{ color: '#340C7F' }}>
                      {achievement.number}
                    </div>
                    <div className="text-gray-600 text-sm mt-1">{achievement.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* About PFIL Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className="space-y-8">
              <div className="space-y-6">
                <h2 className="text-3xl sm:text-4xl font-bold text-gray-900">
                  Our <span style={{ color: '#340C7F' }}>Story</span>
                </h2>
                <div className="space-y-4 text-lg text-gray-600 leading-relaxed">
                  <p>
                    Pooja Finstock International Limited (PFIL), established in 1996, is a trusted and RBI-registered 
                    Non-Banking Financial Company (NBFC), delivering innovative financial solutions for over two decades. 
                    With RBI Registration Number: <strong>B.01.00449</strong>, PFIL operates under the regulatory framework 
                    of the Reserve Bank of India, ensuring complete compliance, transparency, and reliability in all its 
                    financial practices.
                  </p>
                  <p>
                    For nearly 29 years, PFIL has been at the forefront of empowering individuals, entrepreneurs, and small 
                    businesses by providing easy access to personal loans, business financing, and digitally-enabled credit 
                    solutions. Our commitment to financial inclusion and customer-centric services has positioned us as a 
                    preferred NBFC partner across India.
                  </p>
                  <p>
                    Backed by decades of industry experience and a forward-thinking fintech approach, PFIL blends legacy 
                    with innovation to offer seamless lending, secure digital platforms, and customized financial products 
                    tailored to India's evolving economy.
                  </p>
                </div>
              </div>
            </div>

            <div className="relative">
              <img 
                src="https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzR8MHwxfHNlYXJjaHwxfHxmaW5hbmNpYWwlMjBpbnN0aXR1dGlvbnxlbnwwfHx8fDE3NTM3NjE5ODV8MA&ixlib=rb-4.1.0&q=85" 
                alt="PFIL Finance - 29 Years of Financial Excellence and Trust" 
                className="rounded-2xl shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose PFIL */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center space-y-4 mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900">
              Why Choose <span style={{ color: '#340C7F' }}>PFIL Finance?</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-4xl mx-auto">
              At Pooja Finstock International Limited, we are committed to delivering fast, flexible, and secure 
              financial solutions tailored to your needs. With decades of trust and RBI compliance, we combine 
              innovation and customer care to help you meet your goals.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {whyChoose.map((item, index) => (
              <div key={index} className="bg-white rounded-xl p-8 shadow-sm hover:shadow-lg transition-shadow">
                <h3 className="text-xl font-semibold mb-4" style={{ color: '#340C7F' }}>
                  {item.title}
                </h3>
                <p className="text-gray-600 leading-relaxed">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center space-y-4 mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900">
              Our Legacy & <span style={{ color: '#340C7F' }}>Values</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              The principles that guide our decisions and shape our relationships with customers, partners, and communities.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <div key={index} className="bg-gray-50 rounded-xl p-8 text-center space-y-4 hover:shadow-lg transition-shadow">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full text-white" 
                     style={{ backgroundColor: '#340C7F' }}>
                  {value.icon}
                </div>
                <h3 className="text-xl font-semibold text-gray-900">{value.title}</h3>
                <p className="text-gray-600">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* RBI Compliance */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className="relative">
              <img 
                src="https://pfilfinance.com/pfilfinance_about.jpg" 
                alt="Reserve Bank of India (RBI) - Official Regulatory Authority for PFIL Finance NBFC Registration" 
                className="rounded-2xl shadow-2xl"
              />
            </div>

            <div className="space-y-8">
              <div className="space-y-4">
                <h2 className="text-3xl sm:text-4xl font-bold text-gray-900">
                  RBI Registered <span style={{ color: '#340C7F' }}>NBFC</span>
                </h2>
                <p className="text-lg text-gray-600 leading-relaxed">
                  POOJA FINSTOCK INTERNATIONAL LIMITED is a Reserve Bank of India (RBI) registered 
                  Non-Banking Financial Company with Registration Number <strong>B.01.00449</strong>, ensuring complete 
                  regulatory compliance and customer protection.
                </p>
              </div>

              <div className="space-y-4">
                {[
                  'Fully compliant with RBI guidelines and regulations',
                  'Regular audits and inspections by regulatory authorities',
                  'Transparent and ethical business practices',
                  'Customer grievance redressal mechanism in place',
                  'Secure and confidential handling of customer data'
                ].map((item, index) => (
                  <div key={index} className="flex items-center space-x-3">
                    <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0" />
                    <span className="text-gray-700">{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8" style={{ backgroundColor: '#340C7F' }}>
        <div className="max-w-4xl mx-auto text-center space-y-8">
          <h2 className="text-3xl sm:text-4xl font-bold text-white">
            Join Our Family of Satisfied Customers
          </h2>
          <p className="text-xl text-blue-100">
            Experience the difference of working with a trusted, experienced, and customer-focused financial partner.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="inline-flex items-center justify-center px-8 py-4 text-lg font-medium bg-white text-gray-900 rounded-lg hover:bg-gray-100 transition-colors shadow-lg"
            >
              Get Started
              <ArrowRight className="ml-2 h-5 w-5" />
            </Link>
            <Link
              to="/contact"
              className="inline-flex items-center justify-center px-8 py-4 text-lg font-medium text-white border-2 border-white rounded-lg hover:bg-white hover:text-gray-900 transition-colors"
            >
              Contact Us
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;